import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ListenerLayout } from "./Dashboard";
import axios from "axios";
import { 
  CreditCard, CheckCircle, AlertCircle, ExternalLink, 
  RefreshCw, Loader2, Globe, Clock, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListenerSettings() {
  const [searchParams] = useSearchParams();
  const [stripeStatus, setStripeStatus] = useState(null);
  const [profile, setProfile] = useState(null);
  const [timezones, setTimezones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Check for Stripe callback
    const stripeParam = searchParams.get("stripe");
    if (stripeParam === "success") {
      toast.success("Configuración de Stripe completada");
    } else if (stripeParam === "refresh") {
      toast.info("Continúa configurando tu cuenta Stripe");
    }
    
    fetchData();
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [stripeRes, profileRes, tzRes] = await Promise.all([
        axios.get(`${API}/listener/stripe/status`),
        axios.get(`${API}/listener/profile`),
        axios.get(`${API}/timezones`)
      ]);
      setStripeStatus(stripeRes.data);
      setProfile(profileRes.data);
      setTimezones(tzRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (field, value) => {
    setSaving(true);
    try {
      await axios.put(`${API}/listener/profile`, { [field]: value });
      setProfile(prev => ({ ...prev, [field]: value }));
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleOnline = async () => {
    try {
      if (profile?.is_online) {
        await axios.post(`${API}/listener/go-offline`);
        setProfile(prev => ({ ...prev, is_online: false }));
        toast.success("Ya no estás disponible");
      } else {
        await axios.post(`${API}/listener/go-online`);
        setProfile(prev => ({ ...prev, is_online: true }));
        toast.success("¡Estás disponible ahora!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado");
    }
  };

  const connectStripe = async () => {
    setConnecting(true);
    try {
      const res = await axios.post(`${API}/listener/stripe/connect`);
      if (res.data.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      }
    } catch (error) {
      console.error("Error connecting Stripe:", error);
      toast.error(error.response?.data?.detail || "Error al conectar con Stripe");
    } finally {
      setConnecting(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const res = await axios.get(`${API}/listener/stripe/dashboard`);
      if (res.data.dashboard_url) {
        window.open(res.data.dashboard_url, "_blank");
      }
    } catch (error) {
      console.error("Error getting dashboard link:", error);
      toast.error("Error al abrir el panel de Stripe");
    }
  };

  if (loading) {
    return (
      <ListenerLayout title="Configuración">
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </ListenerLayout>
    );
  }

  return (
    <ListenerLayout title="Configuración">
      <div className="space-y-6">
        {/* Timezone Section */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Zona horaria</h2>
              <p className="text-sm text-stone-500">Configura tu zona horaria para la disponibilidad</p>
            </div>
          </div>
          
          <Select 
            value={profile?.timezone || "America/Mexico_City"} 
            onValueChange={(value) => updateProfile("timezone", value)}
            disabled={saving}
          >
            <SelectTrigger className="w-full" data-testid="timezone-select">
              <SelectValue placeholder="Selecciona tu zona horaria" />
            </SelectTrigger>
            <SelectContent>
              {timezones.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Availability Mode Section */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Zap className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Modo de disponibilidad</h2>
              <p className="text-sm text-stone-500">¿Cómo quieres recibir reservas?</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <Select 
              value={profile?.availability_mode || "appointment_only"} 
              onValueChange={(value) => updateProfile("availability_mode", value)}
              disabled={saving}
            >
              <SelectTrigger className="w-full" data-testid="availability-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="appointment_only">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Solo con cita previa
                  </div>
                </SelectItem>
                <SelectItem value="instant">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-green-500" />
                    Disponible ahora (sesiones instantáneas)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Online Toggle (only for instant mode) */}
            {profile?.availability_mode === "instant" && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-800">Estado actual</p>
                    <p className="text-sm text-green-600">
                      {profile?.is_online ? "Estás visible como disponible ahora" : "No estás visible como disponible"}
                    </p>
                  </div>
                  <Button 
                    variant={profile?.is_online ? "destructive" : "default"}
                    onClick={toggleOnline}
                    className={profile?.is_online ? "" : "bg-green-600 hover:bg-green-700"}
                    data-testid="toggle-online-btn"
                  >
                    {profile?.is_online ? "Desconectarme" : "Conectarme ahora"}
                  </Button>
                </div>
              </div>
            )}

            {/* Minimum Notice */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Tiempo mínimo de aviso
              </label>
              <Select 
                value={String(profile?.minimum_notice_hours || 24)} 
                onValueChange={(value) => updateProfile("minimum_notice_hours", parseInt(value))}
                disabled={saving}
              >
                <SelectTrigger className="w-full" data-testid="notice-hours-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Sin aviso (instantáneo)</SelectItem>
                  <SelectItem value="2">2 horas de aviso</SelectItem>
                  <SelectItem value="6">6 horas de aviso</SelectItem>
                  <SelectItem value="12">12 horas de aviso</SelectItem>
                  <SelectItem value="24">24 horas de aviso</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500 mt-1">
                Los usuarios no podrán reservar con menos tiempo de anticipación
              </p>
            </div>
          </div>
        </div>

        {/* Stripe Connect Section */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Pagos con Stripe</h2>
              <p className="text-sm text-stone-500">Configura tu cuenta para recibir pagos</p>
            </div>
          </div>

          {stripeStatus?.connected ? (
            <div className="space-y-4">
              {/* Status indicator */}
              <div className={`flex items-center gap-3 p-4 rounded-lg ${
                stripeStatus.status === "active" 
                  ? "bg-green-50 border border-green-200" 
                  : "bg-amber-50 border border-amber-200"
              }`}>
                {stripeStatus.status === "active" ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">Cuenta activa</p>
                      <p className="text-sm text-green-600">{stripeStatus.message}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Configuración pendiente</p>
                      <p className="text-sm text-amber-600">{stripeStatus.message}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Account details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-stone-500">Pagos habilitados</p>
                  <p className="font-medium text-foreground">
                    {stripeStatus.charges_enabled ? "Sí" : "No"}
                  </p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-stone-500">Retiros habilitados</p>
                  <p className="font-medium text-foreground">
                    {stripeStatus.payouts_enabled ? "Sí" : "No"}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                {stripeStatus.status !== "active" && (
                  <Button onClick={connectStripe} disabled={connecting} data-testid="complete-stripe-setup-btn">
                    {connecting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Conectando...
                      </>
                    ) : (
                      "Completar configuración"
                    )}
                  </Button>
                )}
                <Button variant="outline" onClick={openStripeDashboard} data-testid="open-stripe-dashboard-btn">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ver panel de Stripe
                </Button>
                <Button variant="ghost" onClick={() => fetchData()} data-testid="refresh-stripe-status-btn">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                <p className="text-stone-600">
                  Conecta tu cuenta de Stripe para recibir pagos de tus sesiones. 
                  Stripe es una plataforma segura para procesar pagos.
                </p>
              </div>

              <div className="space-y-2 text-sm text-stone-500">
                <p className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Pagos seguros y automáticos
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Retiros a tu cuenta bancaria
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Panel de control para ver tus ganancias
                </p>
              </div>

              <Button 
                onClick={connectStripe} 
                disabled={connecting}
                className="w-full"
                data-testid="connect-stripe-btn"
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Conectar cuenta Stripe
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Info section */}
        <div className="bg-primary/5 rounded-2xl border border-primary/20 p-6">
          <h3 className="font-semibold text-foreground mb-2">¿Cómo funcionan los pagos?</h3>
          <ul className="space-y-2 text-sm text-stone-600">
            <li>• Cuando un usuario reserva una sesión contigo, paga el precio configurado por el administrador.</li>
            <li>• El pago se divide automáticamente según los porcentajes configurados.</li>
            <li>• Tu parte se transfiere directamente a tu cuenta de Stripe.</li>
            <li>• Puedes ver todas tus transacciones en el panel de Stripe.</li>
          </ul>
        </div>
      </div>
    </ListenerLayout>
  );
}
